import React, { useContext, useEffect, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, InputAdornment, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@material-ui/core";

import EditIcon from "@material-ui/icons/Edit";
import SearchIcon from "@material-ui/icons/Search";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ManagementModal from "../../components/ManagementModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";
import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";

import SocketAccounts from "../../context/Socket/Listeners/SocketAccounts";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CLIENTS") {
    const clients = action.payload;
    const newClients = [];

    clients.forEach((client) => {
      const clientIndex = state.findIndex((c) => c.id === client.id);
      if (clientIndex !== -1) state[clientIndex] = client;
      else newClients.push(client);
    });

    return [...state, ...newClients];
  }

  if (action.type === "UPDATE_CLIENTS") {
    const client = action.payload;
    const clientIndex = state.findIndex((c) => c.id === client.id);

    if (clientIndex !== -1) {
      state[clientIndex] = client;
      return [...state];
    }
    else {
      return [client, ...state];
    }
  }

  if (action.type === "DELETE_CLIENT") {
    const clientId = action.payload;
    const clientIndex = state.findIndex((c) => c.id === clientId);
    if (clientIndex !== -1) state.splice(clientIndex, 1);
    return [...state];
  }

  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  
  clientsList: {
    overflowY: "scroll",
    height: "90%",
    [theme.breakpoints.down("sm")]: { maxHeight: "80%", },
    ...theme.scrollbarStyles,
  },
  
  activeStatus: {
    backgroundColor: "green",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
  },
  
  inactiveStatus: {
    backgroundColor: "gray",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
  },
  
  icon: { color: theme.palette.text.primary, },
  
  actionButton: { "&:hover": { color: theme.palette.primary.main, }, },
  
  floatingButton: {
    transition: "transform 0.3s",
    "&:hover": { transform: "translateY(-5px)", },
  },
}));

const Management = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [clients, dispatch] = useReducer(reducer, []);

  const [searchParam, setSearchParam] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);
  const [clientModalOpen, setClientModalOpen] = useState(false);

  const { setShouldSyncRouteOnMenu } = useGlobalRegisters();



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!DefaultValuesUtils.MANAGER_TENANTS.includes(user?.tenantId?.toString()) || !user.isManager) {
      DefaultFunctionsUtils.redirectToTicketsPage(history);
      setShouldSyncRouteOnMenu(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, user]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchClients = async () => {
        try {
          const { data } = await api.get("/accounts", { params: { searchParam, pageNumber }, });
          dispatch({ type: "LOAD_CLIENTS", payload: data.accounts });
          setHasMore(data.hasMore);
        }
        catch (exception) {
          console.log("Management Use Effect 1 Exception:", exception);
          toastError(exception);
        }
        finally {
          setLoading(false);
        }
      };

      fetchClients();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);




  //  ***************
  //  ** Functions **
  //  ***************
  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenClientModalEdit = (client) => {
    setSelectedClient(client);
    setClientModalOpen(true);
  };

  const handleCloseClientModal = () => {
    setSelectedClient(null);
    setClientModalOpen(false);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber(previousState => previousState + 1);
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <MainContainer>
      <SocketAccounts dispatch={dispatch} />

      <ManagementModal open={clientModalOpen} onClose={handleCloseClientModal} client={selectedClient} />

      <MainHeader>
        <Title>{i18n.t("management.title")}</Title>

        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("management.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            inputProps={{startAdornment: (<InputAdornment position="start"><SearchIcon style={{ color: "gray" }} /></InputAdornment>)}}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.clientsList} onScroll={handleScroll}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" width="3%"></TableCell>
                <TableCell align="center">{i18n.t("management.table.tenantId")}</TableCell>
                <TableCell align="center">{i18n.t("management.table.name")}</TableCell>
                <TableCell align="center">{i18n.t("management.table.url")}</TableCell>
                <TableCell align="center">{i18n.t("management.table.users")}</TableCell>
                <TableCell align="center">{i18n.t("management.table.chips")}</TableCell>
                <TableCell align="center">{i18n.t("management.table.dispatchStatus")}</TableCell>
                <TableCell align="center">{i18n.t("management.table.isMetaApiEnabled")}</TableCell>
                <TableCell align="center">{i18n.t("management.table.actions")}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {clients.map(client => (
                <TableRow key={client.id}>
                  <TableCell align="center"><div className={client.isActive ? classes.activeStatus : classes.inactiveStatus} /></TableCell>
                  <TableCell align="center">{client.id}</TableCell>
                  <TableCell align="center">{client.name}</TableCell>
                  <TableCell align="center">{client.url}</TableCell>
                  <TableCell align="center">{client.totalUsers}</TableCell>
                  <TableCell align="center">{client.totalChips}</TableCell>
                  <TableCell align="center">{client.dispatchEnabledOnBestzap ? "✔️" : "❌"}</TableCell>
                  <TableCell align="center">{client.isMetaApiEnabled ? "✔️" : "❌"}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      className={classes.icon}
                      size="small"
                      onClick={() => handleOpenClientModalEdit(client)}
                    >
                      <EditIcon className={classes.actionButton} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {loading && <TableRowSkeleton columns={9} />}
            </TableBody>
          </Table>
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Management;
